import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { LineReveal, Reveal } from "@/components/site/motion";
import { ClipFrame } from "@/components/site/ClipFrame";
import { SubscribePanel } from "@/components/logbook/SubscribePanel";
import { useLogbookPost } from "@/hooks/useLogbook";
import { formatDate } from "@/lib/api";
import { LINKS } from "@/content/site";

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const PURIFY = {
  FORBID_TAGS: ["style", "script", "form", "input", "button", "noscript"],
  FORBID_ATTR: ["style", "class", "id", "width", "height", "align", "bgcolor", "border", "cellpadding", "cellspacing"],
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "target"],
};

const NavLinkCard = ({ item, dir }) =>
  item ? (
    <Link to={`/logbook/${item.slug}`} className={`group flex flex-col rounded-2xl border border-white/[0.08] p-6 transition-colors hover:border-signal/50 ${dir === "next" ? "sm:items-end sm:text-right" : ""}`} data-testid={`article-${dir}-link`}>
      <span className="eyebrow inline-flex items-center gap-2 text-fg-3">
        {dir === "prev" && <ArrowLeft className="h-3.5 w-3.5" />}
        {dir === "prev" ? "Newer" : "Older"}
        {dir === "next" && <ArrowRight className="h-3.5 w-3.5" />}
      </span>
      <span className="mt-3 text-lg font-[520] leading-snug text-fg group-hover:text-signal">{item.title}</span>
    </Link>
  ) : <div />;

const Missing = ({ notFound }) => (
  <section className="shell pb-32 pt-44" data-testid="article-missing">
    <p className="eyebrow text-fg-3">Logbook</p>
    <h1 className="mt-6 text-5xl font-[560] tracking-[-0.04em] text-fg">{notFound ? "Entry not found." : "This entry couldn't load."}</h1>
    <div className="mt-8 flex flex-wrap gap-6">
      <Link to="/logbook" className="inline-flex items-center gap-2 text-fg hover:text-signal"><ArrowLeft className="h-4 w-4" /> Back to the Logbook</Link>
      <a href={LINKS.beehiiv} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg-2 hover:text-signal">Beehiiv archive <ArrowUpRight className="h-4 w-4" /></a>
    </div>
  </section>
);

export default function Article() {
  const { slug } = useParams();
  const { data, loading, error } = useLogbookPost(slug);
  const post = data?.post;
  const html = useMemo(() => (post?.content_html ? DOMPurify.sanitize(post.content_html, PURIFY) : ""), [post]);

  if (loading) return <section className="shell pb-32 pt-44"><div className="h-[60vh] animate-pulse rounded-3xl bg-white/[0.03]" data-testid="article-loading" /></section>;
  if (error || !post) return <Missing notFound={error === "notfound"} />;

  return (
    <article data-testid="article-page">
      <header className="shell pb-12 pt-32 sm:pt-40">
        <Reveal>
          <Link to="/logbook" className="eyebrow inline-flex items-center gap-2 text-fg-3 hover:text-fg" data-testid="article-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Logbook</Link>
        </Reveal>
        <div className="mx-auto mt-10 max-w-4xl">
          <Reveal>
            <p className="mono text-[12px] uppercase tracking-[0.12em] text-signal">{(post.categories || []).join(" · ") || "Logbook"}</p>
          </Reveal>
          <LineReveal as="h1" play lines={[post.title]} className="mt-5 text-4xl font-[560] leading-[1.02] tracking-[-0.04em] text-fg sm:text-5xl lg:text-6xl" testId="article-title" />
          {post.subtitle && <Reveal delay={0.2}><p className="mt-5 text-xl leading-relaxed text-fg-2">{post.subtitle}</p></Reveal>}
          <Reveal delay={0.3} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-5 text-[14px] text-fg-2">
            <span>{post.author || "x1025"}</span>
            <span className="mono text-[12px] uppercase tracking-[0.1em] text-fg-3">{formatDate(post.published)}</span>
            <span className="mono text-[12px] uppercase tracking-[0.1em] text-fg-3">{post.reading_minutes} min read</span>
            <a href={post.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg hover:text-signal sm:ml-auto" data-testid="article-beehiiv-link">Read on Beehiiv <ArrowUpRight className="h-3.5 w-3.5" /></a>
          </Reveal>
        </div>
      </header>
      {post.image && <div className="shell"><ClipFrame src={post.image} className="mx-auto aspect-[16/9] max-w-5xl" /></div>}
      <div className="shell py-16">
        <div className="logbook-prose mx-auto max-w-[720px]" dangerouslySetInnerHTML={{ __html: html }} data-testid="article-body" data-analytics-section="article" />
      </div>
      <section className="shell grid gap-4 pb-16 sm:grid-cols-2">
        <NavLinkCard item={data?.newer} dir="prev" />
        <NavLinkCard item={data?.older} dir="next" />
      </section>
      <section className="border-t border-white/[0.06] bg-ink-2 py-20">
        <div className="shell grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow text-signal">Subscribe</p>
            <h2 className="mt-5 text-4xl font-[540] leading-[1.02] tracking-[-0.035em] text-fg sm:text-5xl">Get the next entry in your inbox.</h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8"><SubscribePanel title="Join the Logbook." /></div>
        </div>
      </section>
    </article>
  );
}
